import { useState } from "react";
import FormInput from "../../components/controls/FormInput";
import { useTranslation } from "react-i18next";
import {
  CalendarDaysIcon,
  MapPinIcon,
  TagIcon,
  TicketIcon,
  CurrencyDollarIcon,
  LinkIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import EventCardPreview from "./TicketCard";
import ImageUploader from "../../components/controls/ImageUploader";
import DateSelector from "../../components/controls/DatePicker";
import TimeSelector from "../../components/controls/TimeSelector";
import Autocomplete from "../../components/Places/PlacesAutocomplete";
import FormTextarea from "../../components/controls/FormTextArea";
import {
  calculateFromDisplayPrice,
  formatTicketPrice,
} from "./PriceCalculator";
import PriceBreakdown from "./PriceBreakdown";
import { HeartIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";

export default function CreateTicketingForm({ onSubmit }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    location: {
      formattedAddress: "",
      latitude: null,
      longitude: null,
      regionId: 0,
    },
    imageUrl: "",
    eventDate: new Date().toString(),
    maxTickets: 100,
    price: 10.0,
    isFundraiser: false,
  });

  const [errors, setErrors] = useState({});

  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "location"
          ? { ...prev[name], ...value }
          : name === "maxTickets" || name === "price"
          ? Number(value)
          : value,
    }));
  };

  const handleImageChange = (url) => {
    setFormData((prev) => ({ ...prev, imageUrl: url })); // 🔥 met à jour le vrai champ du formulairew
  };

  const toLocalDateTimeString = (date) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )}T${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const priceBreakdown = calculateFromDisplayPrice(formData.price);

    const payload = {
      ...formData,
      price: priceBreakdown.ticketPrice,
    };

    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/Event`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      setErrors(data.errors || {});
    } else {
      setErrors({});
      if (onSubmit) onSubmit(data);
      navigate(`/upcoming-events`);
    }
  };

  return (
    <div className="bg-gray-100 min-h-screen">
      <form
        onSubmit={handleSubmit}
        className="p-4 mx-auto w-full max-w-[1500px] "
      >
        <h2 className="text-3xl font-bold text-primary text-center mb-4">
          {t("ticketing.createTitle")}
        </h2>
        <div className="flex flex-col md:flex-row gap-6 ">
          {/* Formulaire w*/}

          <div className="flex-1 bg-white p-6 rounded-xl shadow space-y-4 flex flex-col">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Form inputs */}
              <div className="flex-1 space-y-4">
                <FormInput
                  label={t("ticketing.eventNameLabel")}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t("ticketing.eventNamePlaceholder")}
                  error={errors.name}
                  required
                  icon={<TagIcon className="w-5 h-5 text-gray-500" />}
                />
                <FormTextarea
                  label={t("modal.description")}
                  name="description"
                  value={formData.description}
                  onChange={(name, value) => handleChange(name, value)}
                  placeholder={t("modal.description")}
                  as="textarea"
                  required
                />
                <Autocomplete
                  onSelect={(e) => {
                    handleChange("location", e);
                  }}
                  initialAddress=""
                  showOptional={false}
                  label={t("ticketing.locationLabel")}
                  placeholder={t("ticketing.locationPlaceholder")}
                />

                <FormInput
                  label={t("ticketing.maxTicketsLabel")}
                  name="maxTickets"
                  type="number"
                  min="1"
                  value={formData.maxTickets}
                  onChange={handleChange}
                  placeholder="100"
                  error={errors.maxTickets}
                  required
                  icon={<TicketIcon className="w-5 h-5 text-gray-500" />}
                />

                <div className="space-y-3">
                  <h3 className="text-base font-semibold text-primary">
                    {t("ticketing.priceLabel")}
                  </h3>

                  <div className="bg-gray-50 p-4 rounded-lg border space-y-3">
                    <h4 className="text-sm font-semibold text-gray-800">
                      {t("ticketing.priceCalculatorTitle")}
                    </h4>

                    <FormInput
                      label={t("ticketing.displayPriceLabel")}
                      name="price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={handleChange}
                      placeholder="40.00"
                      error={errors.price}
                      required
                      icon={
                        <CurrencyDollarIcon className="w-5 h-5 text-gray-500" />
                      }
                    />

                    {formData.price > 0 && (
                      <>
                        <PriceBreakdown
                          calc={calculateFromDisplayPrice(formData.price)}
                        />
                        <p className="text-xs text-gray-500">
                          {t("ticketing.priceCalculatorHint", {
                            amount: formatTicketPrice(
                              calculateFromDisplayPrice(formData.price)
                                .ticketPrice
                            ),
                          })}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                <FormInput
                  label={t("meetingUrl")}
                  name="meetingUrl"
                  type="text"
                  value={formData.meetingUrl || ""}
                  onChange={handleChange}
                  icon={<LinkIcon className="w-5 h-5 text-gray-500" />}
                />

                <FormInput
                  label={t("meetingPassword")}
                  name="meetingPassword"
                  type="text"
                  value={formData.meetingPassword || ""}
                  onChange={handleChange}
                  placeholder=""
                  icon={<LockClosedIcon className="w-5 h-5 text-gray-500" />}
                />

                <ImageUploader
                  initialImageUrl={formData.imageUrl || ""}
                  onTempImageChange={handleImageChange}
                />
              </div>

              {/* Calendar + image */}
              <div className="md:w-1/2 space-y-4">
                <DateSelector
                  label={t("ticketing.eventDateLabel")}
                  value={formData.eventDate}
                  className=""
                  onChange={(newDateOnly) => {
                    if (!newDateOnly) return;

                    const oldDate = formData.eventDate
                      ? new Date(formData.eventDate)
                      : null;
                    const updatedDate = new Date(newDateOnly);

                    if (oldDate) {
                      updatedDate.setHours(oldDate.getHours());
                      updatedDate.setMinutes(oldDate.getMinutes());
                    }

                    setFormData((prev) => ({
                      ...prev,
                      eventDate: updatedDate,
                    }));
                  }}
                />
                <TimeSelector
                  label={t("modal.startTime")}
                  value={
                    formData.eventDate
                      ? `${new Date(formData.eventDate)
                          .getHours()
                          .toString()
                          .padStart(2, "0")}:${new Date(formData.eventDate)
                          .getMinutes()
                          .toString()
                          .padStart(2, "0")}`
                      : ""
                  }
                  onChange={(time) => {
                    if (!formData.eventDate || !time.includes(":")) return;
                    const [h, m] = time.split(":").map(Number);
                    const updated = new Date(formData.eventDate);
                    updated.setHours(h);
                    updated.setMinutes(m);
                    setFormData((prev) => ({
                      ...prev,
                      eventDate: updated,
                    }));
                  }}
                />

                <div className="flex flex-col gap-1 text-primary">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="checkbox-pro"
                      name="isFundraiser"
                      checked={formData.isFundraiser}
                      onChange={(e) =>
                        handleChange(e.target.name, e.target.checked)
                      }
                    />
                    {formData.isFundraiser && (
                      <HeartIcon className="w-5 h-5 text-ananas" />
                    )}
                    <span className="text-sm">
                      {t("ticketing.isFundraiserLabel") ||
                        "Cet événement est à but caritatif"}
                    </span>
                  </label>

                  <p className="text-xs text-gray-500">
                    {t("ticketing.isFundraiserInfo") ||
                      "Si cette case est cochée, il est possible qu'une vérification soit effectuée pour confirmer qu'il s'agit réellement d'un événement bénéfice."}
                  </p>
                </div>
              </div>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              className="bg-primary text-white px-6 py-2 rounded-full hover:bg-hoverCyan transition mx-auto"
            >
              {t("ticketing.saveEventButton")}
            </button>
          </div>
          <div className="border-right border m-4 "></div>

          <div className="w-full md:max-w-sm flex-col md:flex">
            <h2 className="text-xl font-bold text-primary mb-2">
              {t("ticketing.previewTitle")} :
            </h2>{" "}
            <EventCardPreview
              name={formData.name}
              date={formData.eventDate}
              location={formData.location}
              price={formData.price.toFixed(2)}
              imageUrl={formData.imageUrl}
              isFundraiser={formData.isFundraiser}
            />
          </div>
        </div>
      </form>
    </div>
  );
}
